/* eslint-disable indent */

/* eslint-disable no-console */
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { Player } from '@remotion/player';
import { useQueryClient } from '@tanstack/react-query';
import { prefetch } from 'remotion';

import { MainVideoTemplate } from '@/Remotion/MainVideoTemplate';
import { Button, Label, LABEL_TYPE, ProgressBar } from '@/components/generic';
import { ButtonVariant } from '@/components/generic/Button/button-config';
import { VideoTypeSelector } from '@/components/generic/VideoTypeSelector/VideoTypeSelector';
import {
  AUDIO,
  DEFAULT_CLIP_DURATION,
  DEFAULT_TEXT_DURATION,
  DOWNLOAD_VIDEO_EXTENSION,
  IMAGE,
  INTRO_OUTRO_DEFAULT_DURATION,
  MAX_PERSONAL_VIDEO_DURATION,
  REMOTION_PLAYER_FPS,
  REMOTION_PREFETCHING,
  STOCK_CLIP,
  STORAGE_LIMIT_EXCEEDED_ERROR_CODE,
  TIME_LIMIT_EXCEEDED_ERROR_CODE,
  TRIBUTE_VIDEO_LIMIT_EXCEEDED_ERROR_CODE,
  VIDEO
} from '@/constants';
import { AuthContext } from '@/contexts';
import {
  AssetStatus,
  PERMISSION_TYPE,
  TributeVideoBackgroundType,
  TributeVideoExportStatus,
  TributeVideoFailedErrors
} from '@/enums';
import { BlobLocation } from '@/enums/BlobLocationEnum';
import { blurImageFile, uploadFileWithSasToken } from '@/helpers';
import { getMessageForProgressBar } from '@/helpers/TributeVideoProgressHelper';
import { showToast, ToastType } from '@/helpers/toast';
import { DownloadIcon } from '@/resources';
import {
  assetEndpoints,
  useGenerateTributeVideo,
  useGetImageMovementsByMovementBundleId,
  useGetTributeVideoQuery,
  useGetVideoThemeById
} from '@/services';
import {
  useCancelVideoRendering,
  useGetTributeVideoExportStatus
} from '@/services/tributeVideoExport.service';
import { useGetClipsByVideoThemeIdQuery } from '@/services/videoThemeClips.service';
import { useGetTextByVideoThemeIdQuery } from '@/services/videoThemeText.service';
import { TestIds } from '@/shared/test-ids';
import {
  SasTokenPayload,
  TributeVideoAsset,
  TributeVideoOptionsType,
  TributeVideoStatusType,
  UpdateAssetBlurredUrlPayload,
  UpdateCreateTributeVideoPayload,
  VideoThemeClip,
  VideoThemeText
} from '@/types';

const CLIP_TYPES = {
  Intro: 'Intro',
  Background: 'Background',
  Middle: 'Middle',
  Outro: 'Outro'
};

type AudioTrack = { src: string; duration: number; label?: string };

const VIDEO_TYPE_OPTIONS = ['fullVideo', 'previewVideo'];

type UploadVideoProps = {
  tributeVideoData: UpdateCreateTributeVideoPayload;
  assets: TributeVideoAsset[];
  titleText?: string;
  birthDeathText?: string;
  deceasedNameText?: string;
  tributeVideoOptions: TributeVideoOptionsType;
};

export const UploadVideo = ({
  tributeVideoData,
  assets,
  titleText,
  birthDeathText,
  deceasedNameText,
  tributeVideoOptions
}: UploadVideoProps) => {
  const { permissions } = useContext(AuthContext);
  const [previewLastExportId, setPreviewLastExportId] = useState();
  const [fullLastExportId, setFullLastExportId] = useState();
  const { tributeVideoId } = useParams();
  const [isFullCompleted, setIsFullCompleted] = useState<boolean>(false);
  const [isPreviewCompleted, setIsPreviewCompleted] = useState<boolean>(false);
  const [t] = useTranslation('common');
  const [isCancelingFullVideo, setIsCancelingFullVideo] = useState(false);
  const [isCancelingPreviewVideo, setIsCancelingPreviewVideo] = useState(false);
  const [showFullProgressBar, setShowFullProgressBar] = useState(false);
  const [showPreviewProgressBar, setShowPreviewProgressBar] = useState(false);
  const [isFull, setIsFull] = useState<boolean>(true);
  const queryClient = useQueryClient();
  const [shownErrorExportIds, setShownErrorExportIds] = useState<number[]>([]);
  const [displayRemotion, setDisplayRemotion] = useState<boolean>(false);
  const [areAssetsPrefetched, setAreAssetsPrefetched] = useState<boolean>(false);
  const [assetDownloadPercentage, setAssetDownloadPercentage] = useState<number>(0);
  const [isPreparingAssets, setIsPreparingAssets] = useState<boolean>(false);

  const { data: tributeVideo, refetch: refetchTributeVideoData } = useGetTributeVideoQuery(
    Number(tributeVideoId)
  );

  const { data: videoThemeClips } = useGetClipsByVideoThemeIdQuery(
    Number(tributeVideoData.videoThemeId)
  );

  const { data: videoThemeText } = useGetTextByVideoThemeIdQuery(
    Number(tributeVideoData.videoThemeId)
  );

  const { data: imageMovements } = useGetImageMovementsByMovementBundleId(
    Number(tributeVideoData.movementBundleId)
  );

  const { data: themeData } = useGetVideoThemeById(Number(tributeVideoData.videoThemeId));

  const hasGlobAccess = permissions?.includes(PERMISSION_TYPE.GLOBAL_ACCESS);

  const activeRefetchInterval = useMemo(() => {
    return isFull ? (isFullCompleted ? 0 : 300) : isPreviewCompleted ? 0 : 300;
  }, [isFull, isFullCompleted, isPreviewCompleted]);

  const { data: lastExportData } = useGetTributeVideoExportStatus(
    Number(isFull ? fullLastExportId : previewLastExportId),
    {
      refetchInterval: activeRefetchInterval,
      onSuccess: (response) => {
        if (response?.data?.status === TributeVideoExportStatus.Completed) {
          refetchTributeVideoData();
          isFull ? setIsFullCompleted(true) : setIsPreviewCompleted(true);
        }
      },
      onError: (error) => {
        console.log(error);
      }
    }
  );

  const { mutateAsync: cancelVideoRendering } = useCancelVideoRendering(queryClient, {
    onSuccess: () => {
      queryClient.invalidateQueries(['tributeVideo', tributeVideoId]);
      refetchTributeVideoData();
    },
    onError: () => undefined
  });

  const { mutateAsync: generateTributeVideo, isLoading: isLoadingGenerateTributeVideo } =
    useGenerateTributeVideo(queryClient, {
      onSuccess: (response) => {
        if (isFull) {
          setFullLastExportId(response?.data?.id);
        } else {
          setPreviewLastExportId(response?.data?.id);
        }
        refetchTributeVideoData();
      },
      onError: (error) => {
        const backendError = error.response?.data?.ErrorMessages?.[0];
        const message = backendError?.DisplayMessage ?? t('regenerateTributeVideoError');
        const errorCode = backendError?.Code;
        const isLimitExceeded =
          !!errorCode &&
          [
            TRIBUTE_VIDEO_LIMIT_EXCEEDED_ERROR_CODE,
            STORAGE_LIMIT_EXCEEDED_ERROR_CODE,
            TIME_LIMIT_EXCEEDED_ERROR_CODE
          ].includes(errorCode);

        if (isLimitExceeded) {
          showToast(ToastType.WARNING, {
            text: message,
            autoClose: false
          });
          return;
        }

        showToast(ToastType.ERROR, message);
      }
    });

  const blurAndUploadImage = async (imageUrl: string, eventId: number): Promise<string | null> => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    const extension = imageUrl.split('?')[0].split('.').pop() ?? 'jpg';
    const inputFile = new File([blob], `input.${extension}`, { type: blob.type });

    const blurredFile = await blurImageFile(inputFile);
    if (!blurredFile) {
      return null;
    }

    const sasPayload: SasTokenPayload = {
      id: eventId,
      isPublic: false,
      isThumbnail: false,
      assetName: '',
      extension: '.jpg',
      blobLocation: BlobLocation.EVENTS
    };

    const sasTokenData = await assetEndpoints.createSasToken(sasPayload);
    return uploadFileWithSasToken({ file: blurredFile, sasTokenData });
  };

  const handleStartRendering = async () => {
    if (
      tributeVideo?.data?.options?.backgroundType ===
      TributeVideoBackgroundType[TributeVideoBackgroundType.ContentBased]
    ) {
      setIsPreparingAssets(true);
      try {
        const imageAssets = (tributeVideo?.data?.assets ?? [])
          .filter((a: TributeVideoAsset) => a.asset.assetType === IMAGE)
          .filter(
            (a: TributeVideoAsset, index: number, self: TributeVideoAsset[]) =>
              self.findIndex((x) => x.asset.id === a.asset.id) === index
          );

        for (const tributeAsset of imageAssets) {
          try {
            if (tributeAsset.asset.blurredImageUrl) {
              continue;
            }

            const blurredUrl = await blurAndUploadImage(
              tributeAsset.asset.assetUrl,
              tributeVideo.data.eventId
            );
            if (blurredUrl) {
              const payload: UpdateAssetBlurredUrlPayload = {
                id: tributeAsset.asset.id,
                blurredImageUrl: blurredUrl
              };
              await assetEndpoints.updateAssetBlurredUrl(payload);
            }
          } catch (e) {
            console.error('Failed to blur image for asset:', tributeAsset.asset.id, e);
          }
        }
      } finally {
        setIsPreparingAssets(false);
      }
    }

    if (isFull) {
      try {
        setShowFullProgressBar(true);
        await generateTributeVideo({
          id: tributeVideoId as string,
          isPreview: false
        });
      } catch {
        setShowFullProgressBar(false);
      }
    } else {
      try {
        setShowPreviewProgressBar(true);
        await generateTributeVideo({
          id: tributeVideoId as string,
          isPreview: true
        });
      } catch {
        setShowPreviewProgressBar(false);
      }
    }
  };

  const handleCancelRendering = async () => {
    if (isFull) {
      if (fullLastExportId) {
        setIsCancelingFullVideo(true);
        await cancelVideoRendering(fullLastExportId);
      }
    } else {
      if (previewLastExportId) {
        setIsCancelingPreviewVideo(true);
        await cancelVideoRendering(previewLastExportId);
      }
    }
  };

  useEffect(() => {
    setIsCancelingFullVideo(false);
  }, [showFullProgressBar]);

  useEffect(() => {
    setIsCancelingPreviewVideo(false);
  }, [showPreviewProgressBar]);

  useEffect(() => {
    queryClient.invalidateQueries(['tributeVideo', tributeVideoId]);
    refetchTributeVideoData();

    if ((isFull && isCancelingFullVideo) || (!isFull && isCancelingPreviewVideo)) {
      handleCancelRendering();
    }
  }, [lastExportData?.data]);

  const assetsToPrefetch = useMemo(
    () => [
      ...assets.map((a) => a.asset.assetUrl),
      ...(videoThemeClips?.data ?? []).map((c: VideoThemeClip) => c.asset.assetUrl)
    ],
    [assets, videoThemeClips?.data]
  );

  useEffect(() => {
    const started: Array<{ url: string; handle: ReturnType<typeof prefetch> }> = [];

    setAreAssetsPrefetched(false);
    setAssetDownloadPercentage(0);

    for (const url of assetsToPrefetch.filter(Boolean)) {
      started.push({ url, handle: prefetch(url, { method: 'blob-url' }) });
    }

    let done = 0;

    const waitPromises = started.map(({ url, handle }) =>
      handle
        .waitUntilDone()
        .catch((e) => {
          console.error('Prefetch failed:', url, e);
        })
        .finally(() => {
          done += 1;
          setAssetDownloadPercentage(Math.floor((done / started.length) * 100));
        })
    );

    Promise.allSettled(waitPromises).then(() => {
      setAreAssetsPrefetched(true);
    });

    return () => {
      started.forEach(({ handle }) => handle.free());
    };
  }, [assetsToPrefetch]);

  useEffect(() => {
    const isInProgress = tributeVideo?.data?.exports?.some(
      (item: TributeVideoStatusType) =>
        item?.status === TributeVideoExportStatus.InProgress ||
        item?.status === TributeVideoExportStatus.RenderingCompleted ||
        item?.status === TributeVideoExportStatus.Pending ||
        item?.status === TributeVideoExportStatus.Preparing
    );

    const exportStatusItem = tributeVideo?.data?.exports?.find(
      (item: TributeVideoStatusType) =>
        item?.isPreview === !isFull &&
        item?.status !== TributeVideoExportStatus.Completed &&
        item?.status !== TributeVideoExportStatus.Failed &&
        item?.status !== TributeVideoExportStatus.Canceled
    );
    if (isFull) {
      if (isInProgress && exportStatusItem) {
        setFullLastExportId(exportStatusItem?.id);
        setShowFullProgressBar(true);
      } else {
        setShowFullProgressBar(false);
      }
    } else {
      if (isInProgress && exportStatusItem) {
        setPreviewLastExportId(exportStatusItem?.id);
        setShowPreviewProgressBar(true);
      } else {
        setShowPreviewProgressBar(false);
      }
    }
  }, [tributeVideo?.data?.exports, isFull]);

  useEffect(() => {
    if (
      lastExportData?.data?.status === TributeVideoExportStatus.Failed &&
      !shownErrorExportIds.includes(lastExportData?.data?.id)
    ) {
      let errMessage;
      switch (lastExportData?.data?.errorType) {
        case TributeVideoFailedErrors.Unknown:
          errMessage = 'unknownError';
          break;
        case TributeVideoFailedErrors.MaxPreparingTime:
          errMessage = 'maxPreparingTime';
          break;
        case TributeVideoFailedErrors.PreparingFailure:
          errMessage = 'preparingFailure';
          break;
        case TributeVideoFailedErrors.RenderingFailure:
          errMessage = 'renderingFailure';
          break;
        case TributeVideoFailedErrors.StorageLimitFailure:
          errMessage = 'storageLimitExceeded';
          break;
        default:
          errMessage = 'unknownError';
      }

      if (errMessage === 'storageLimitExceeded') {
        showToast(ToastType.WARNING, {
          text: t(errMessage),
          autoClose: false
        });
      } else {
        showToast(ToastType.ERROR, t(errMessage));
      }

      setShownErrorExportIds((prev) => [...prev, lastExportData?.data?.id]);

      if (isFull) {
        setShowFullProgressBar(false);
      } else {
        setShowPreviewProgressBar(false);
      }
    }
  }, [lastExportData?.data?.status]);

  useEffect(() => {
    if (
      lastExportData?.data?.status === TributeVideoExportStatus.Canceled ||
      tributeVideo?.data?.status === AssetStatus.CONTENT_PREPARED
    ) {
      if (isFull) {
        setShowFullProgressBar(false);
        setIsCancelingFullVideo(false);
      } else {
        setShowPreviewProgressBar(false);
        setIsCancelingPreviewVideo(false);
      }
    }
  }, [tributeVideo?.data?.status]);

  const audioTracks: AudioTrack[] = useMemo(
    () =>
      assets
        .filter((a) => a.asset.assetType === AUDIO)
        .map((m) => ({
          label: m.asset.assetName || '',
          src: m.asset.assetUrl ?? '',
          duration: Math.max(0, Math.floor(m.asset.duration ?? 0))
        }))
        .filter((t) => !!t.src),
    [assets]
  );

  const nameOfTheDeceasedFromStart = videoThemeText?.data?.find(
    (vtt: VideoThemeText) => vtt.timeFromStart === true && vtt.value === '{nameOfDeceased}'
  );
  const nameOfTheDeceasedFromEnd = videoThemeText?.data?.find(
    (vtt: VideoThemeText) => vtt.timeFromStart === false && vtt.value === '{nameOfDeceased}'
  );
  const title = videoThemeText?.data?.find(
    (vtt: VideoThemeText) => vtt.value === 'In memoriam' || vtt.value === '{title}'
  );
  const birthDeath = videoThemeText?.data?.find(
    (vtt: VideoThemeText) => vtt.value === '{birthDeath}'
  );

  const imageAssetsDuration = useMemo(
    () =>
      assets.filter((asset) => asset.asset.assetType === IMAGE).length *
      (tributeVideoData.options?.durationOfSingleImage ?? 0),
    [assets]
  );

  const videoAssetsDuration = useMemo(
    () =>
      assets
        .filter((a) => a.asset.assetType === VIDEO || a.asset.assetType === STOCK_CLIP)
        .reduce((total, a) => {
          const duration = a.asset?.duration
            ? Math.min(a.asset?.duration, MAX_PERSONAL_VIDEO_DURATION)
            : DEFAULT_CLIP_DURATION;
          return total + duration;
        }, 0),
    [assets]
  );

  const videoDuration = useMemo(() => {
    if (tributeVideoData.options?.musicEnabled) {
      const musicAssets = assets
        .filter((asset) => asset.asset.assetType === AUDIO)
        .reduce((total, asset) => {
          return total + (asset.asset.duration ?? 0);
        }, 0);
      return (
        musicAssets + (themeData?.data.musicStartDelay ?? 0) + (themeData?.data.musicEndDelay ?? 0)
      );
    } else {
      let themeDuration =
        (videoThemeClips?.data.find((clip: VideoThemeClip) => clip.type === CLIP_TYPES.Intro)?.asset
          .duration ?? 0) +
        (videoThemeClips?.data.find((clip: VideoThemeClip) => clip.type === CLIP_TYPES.Outro)?.asset
          .duration ?? 0);

      if (!tributeVideoData.options?.introEnabled && !tributeVideoData.options?.outroEnabled) {
        themeDuration =
          2 * INTRO_OUTRO_DEFAULT_DURATION +
          (tributeVideoData.options?.textsEnabled ? DEFAULT_TEXT_DURATION : 0);
        return themeDuration + imageAssetsDuration + videoAssetsDuration;
      } else if (!tributeVideoData.options.introEnabled && tributeVideoData.options.outroEnabled) {
        themeDuration =
          themeDuration -
          (videoThemeClips?.data.find((clip: VideoThemeClip) => clip.type === CLIP_TYPES.Intro)
            ?.asset.duration ?? 0) +
          INTRO_OUTRO_DEFAULT_DURATION +
          (tributeVideoData.options.textsEnabled ? DEFAULT_TEXT_DURATION : 0);
        return themeDuration + imageAssetsDuration + videoAssetsDuration;
      } else if (
        tributeVideoData?.options?.introEnabled &&
        !tributeVideoData?.options?.outroEnabled
      ) {
        themeDuration =
          themeDuration -
          (videoThemeClips?.data.find((clip: VideoThemeClip) => clip.type === CLIP_TYPES.Outro)
            ?.asset.duration ?? 0) +
          INTRO_OUTRO_DEFAULT_DURATION;
        return themeDuration + imageAssetsDuration + videoAssetsDuration;
      }

      return themeDuration + imageAssetsDuration + videoAssetsDuration;
    }
  }, [assets, tributeVideoData.options?.musicEnabled, themeData, videoThemeClips]);

  return (
    <div>
      <div className='mb-8 h-full items-stretch md:flex'>
        <div className='flex size-full flex-col justify-between md:w-1/2'>
          <Label
            text={t('pleaseReview')}
            type={LABEL_TYPE.PARAGRAPH1}
            className='mt-3 md:mt-6 md:!font-semibold'
          />
          <div className='flex'>
            <VideoTypeSelector
              options={VIDEO_TYPE_OPTIONS}
              selected={isFull ? 'fullVideo' : 'previewVideo'}
              onChange={() => {
                setIsFull(!isFull);
              }}
            />
            {hasGlobAccess && (
              <Button
                className={`ml-2 mt-5 rounded-md px-4 py-2 transition-colors duration-200 ${
                  displayRemotion
                    ? ''
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300 active:scale-95'
                }`}
                handleOnClick={() => {
                  setDisplayRemotion(!displayRemotion);
                }}>
                Remotion
              </Button>
            )}
          </div>
        </div>

        <div className='mt-1 flex w-full items-end justify-center md:w-1/2 md:justify-end'>
          <Button
            data-testid={
              isFull
                ? TestIds.tributeVideo.generateFullVideoButton
                : TestIds.tributeVideo.generatePreviewButton
            }
            variant={ButtonVariant.PRIMARY}
            disabled={
              isPreparingAssets ||
              (showFullProgressBar || showPreviewProgressBar
                ? isFull
                  ? isCancelingFullVideo
                  : isCancelingPreviewVideo
                : false)
            }
            handleOnClick={
              (isFull && showFullProgressBar) || (!isFull && showPreviewProgressBar)
                ? handleCancelRendering
                : handleStartRendering
            }
            className='!lg:px-6 mt-2 w-full !px-2 md:mt-0 md:w-[250px]'>
            {t(
              isFull && !showFullProgressBar
                ? 'generateFullVideo'
                : !isFull && !showPreviewProgressBar
                  ? 'generatePreview'
                  : 'cancelRendering'
            )}
          </Button>
        </div>
      </div>

      <div className='my-4 flex w-full flex-col items-center justify-center sm:my-3 md:items-start'>
        {!isPreparingAssets &&
          (isFull
            ? !showFullProgressBar && tributeVideo?.data?.tributeVideoUrl
            : !showPreviewProgressBar && tributeVideo?.data?.previewVideoUrl) && (
            <div className='relative flex justify-center'>
              <>
                {isFull && (
                  <a
                    style={{ backgroundColor: 'rgba(255, 255, 255, .8)', cursor: 'pointer' }}
                    href={tributeVideo?.data?.tributeVideoUrl}
                    download={tributeVideo?.data?.name + DOWNLOAD_VIDEO_EXTENSION}
                    className='absolute right-0 top-0 z-10 mr-4 mt-4 flex size-9 items-center justify-center rounded-md bg-primary-white'
                    onClick={(e) => e.stopPropagation()}>
                    <DownloadIcon />
                  </a>
                )}
                <video
                  src={
                    isFull
                      ? tributeVideo?.data?.tributeVideoUrl
                      : tributeVideo?.data?.previewVideoUrl
                  }
                  controls
                  className='h-[200px] w-full rounded-xl sm:h-[400px]'
                />
              </>
            </div>
          )}

        {isPreparingAssets && (
          <ProgressBar
            progress={0}
            message={t('preparingAssetsForRendering')}
            isIndeterminateMode={true}
          />
        )}

        {((isFull && showFullProgressBar) || (!isFull && showPreviewProgressBar)) &&
          lastExportData?.data && (
            <ProgressBar
              progress={lastExportData?.data?.progress || 0}
              message={
                getMessageForProgressBar(
                  lastExportData?.data?.status,
                  lastExportData?.data?.progress
                ) as string
              }
              isIndeterminateMode={
                isLoadingGenerateTributeVideo ||
                lastExportData?.data?.status === TributeVideoExportStatus.Preparing ||
                lastExportData?.data?.status === TributeVideoExportStatus.Pending ||
                lastExportData?.data?.status === TributeVideoExportStatus.RenderingCompleted
              }
            />
          )}

        {displayRemotion && (
          <div className='w-full'>
            <Label text='Remotion video preview:' className='mb-1 mt-2' />
            {areAssetsPrefetched ? (
              <Player
                component={MainVideoTemplate}
                inputProps={{
                  assets: assets,
                  introSrc: videoThemeClips?.data.find(
                    (clip: VideoThemeClip) => clip.type === CLIP_TYPES.Intro
                  ).asset.assetUrl,
                  mainSrc: videoThemeClips?.data
                    .filter((clip: VideoThemeClip) => clip.type === CLIP_TYPES.Background)
                    .map((clip: VideoThemeClip) => clip.asset.assetUrl),
                  outroSrc: videoThemeClips?.data.find(
                    (clip: VideoThemeClip) => clip.type === CLIP_TYPES.Outro
                  ).asset.assetUrl,
                  introFrames: tributeVideoData.options?.introEnabled
                    ? videoThemeClips?.data.find(
                        (clip: VideoThemeClip) => clip.type === CLIP_TYPES.Intro
                      ).asset.duration * REMOTION_PLAYER_FPS
                    : 0,
                  mainFrames: videoThemeClips?.data
                    .filter((clip: VideoThemeClip) => clip.type === CLIP_TYPES.Background)
                    .map(
                      (clip: VideoThemeClip) => (clip.asset.duration ?? 0) * REMOTION_PLAYER_FPS
                    ),
                  outroFrames: tributeVideoData.options?.outroEnabled
                    ? videoThemeClips?.data.find(
                        (clip: VideoThemeClip) => clip.type === CLIP_TYPES.Outro
                      ).asset.duration * REMOTION_PLAYER_FPS
                    : 0,
                  hasIntro: !!tributeVideoData.options?.introEnabled,
                  hasOutro: !!tributeVideoData.options?.outroEnabled,
                  audioTracks: audioTracks,
                  textEnabled: !!tributeVideoData.options?.textsEnabled,
                  nameOfTheDeceasedFromStart: nameOfTheDeceasedFromStart,
                  nameOfTheDeceasedFromEnd: nameOfTheDeceasedFromEnd,
                  title: title,
                  birthDeath: birthDeath,
                  titleText: title && title.value === 'In memoriam' ? title.value : titleText,

                  birthDeathText: birthDeathText,
                  deceasedNameText: deceasedNameText,
                  hasMusic: !!tributeVideoData.options?.musicEnabled,
                  imageBordersEnabled: tributeVideoOptions.imageBordersEnabled,
                  imageBorder: tributeVideoOptions.imageBordersEnabled
                    ? (tributeVideoOptions?.barsColor ?? '#' + themeData?.data.barsColor)
                    : undefined,
                  movementBundleId: tributeVideoData.movementBundleId ?? 1,
                  imageMovements: imageMovements?.data
                }}
                durationInFrames={videoDuration * REMOTION_PLAYER_FPS}
                compositionWidth={1920}
                compositionHeight={1080}
                fps={REMOTION_PLAYER_FPS}
                style={{ width: 710, aspectRatio: '16 / 9', borderRadius: '12px' }}
                controls
              />
            ) : (
              <div className='my-4 w-full'>
                <div className='w-full'>
                  <ProgressBar
                    progress={assetDownloadPercentage}
                    message={
                      getMessageForProgressBar(
                        REMOTION_PREFETCHING,
                        assetDownloadPercentage
                      ) as string
                    }
                    isIndeterminateMode={false}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
